import {formatOrderCar} from '../shared/order-car-display';
import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {DataService} from '../services/data.service';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {ExcelService} from '../services/excel.service';
import {DialogCarComponent} from '../dialog-car/dialog-car.component';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {TableSortState} from '../table-sort';
import {Car, Driver} from '../models/api.models';
import {PermissionService} from '../services/permission.service';
import {AdminPermissions} from '../models/admin-permissions';

@Component({
    selector: 'app-cars',
    templateUrl: './cars.component.html',
    styleUrls: ['./cars.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CarsComponent implements OnInit, OnDestroy {

    protected externalCars = false;

    @ViewChild('dialogsContainer', {read: ViewContainerRef, static: true}) dialogsContainer;
    dialogRef: ComponentRef<DialogCarComponent>;

    carsData: Car[] = [];
    filteredCarsData: Car[] = [];
    pageSize = 25;
    currentPage = 1;
    tableSort = new TableSortState();
    isLoadingCars = true;
    isCarsRequestInProgress = false;
    searchText: string;
    cityFilter = '';
    companyFilter = '';
    priceFilter = '';
    suspendedFilter = '';
    canExportData = false;
    dataChangedCarsSubscription: Subscription;
    dataChangedSessionSubscription: Subscription;

    constructor(private dataService: DataService,
                private restService: RestService,
                private uiBlockerService: UIBlockerService,
                private excelService: ExcelService,
                private permissionService: PermissionService) {
    }

    ngOnInit() {
        this.setCarsData(this.externalCars ? [] : this.dataService.getCars());
        if (!this.externalCars) {
            this.dataChangedCarsSubscription = this.dataService.dataChangedCars.subscribe(() => {

                this.setCarsData(this.dataService.getCars());
            });
        }
        this.checkIfUserCanExport();
        this.dataChangedSessionSubscription = this.dataService.dataChangedSession.subscribe(() => {

            this.checkIfUserCanExport();
            this.getCarsData();
        });
        this.getCarsData();
    }

    ngOnDestroy() {
        if (!isNullOrUndefined(this.dataChangedCarsSubscription)) {
            this.dataChangedCarsSubscription.unsubscribe();
        }

        if (!isNullOrUndefined(this.dataChangedSessionSubscription)) {
            this.dataChangedSessionSubscription.unsubscribe();
        }
    }

    getCarsData(showLoading = true) {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isCarsRequestInProgress) {
            return;
        }

        this.isCarsRequestInProgress = true;
        if (showLoading) {
            this.isLoadingCars = true;
        }
        const request = this.externalCars
            ? this.restService.getExternalCars()
            : this.restService.getCars();
        request
            .pipe(
                finalize(() => {
                    this.isCarsRequestInProgress = false;
                    if (showLoading) {
                        this.isLoadingCars = false;
                    }
                    this.uiBlockerService.unBlockUI();
                })
            )
            .subscribe((res) => {
                if (this.dataService.isResponseSuccessful(res)) {
                    if (this.externalCars) {
                        this.setCarsData(res.Cars || []);
                    } else {
                        this.dataService.setCars(res.Cars || []);
                    }
                }
            });
    }

    setCarsData(cars: Car[]) {
        this.carsData = isNullOrUndefined(cars) ? [] : cars;
        this.updateFilteredCars(false);
    }

    onSearchTextChanged(searchText: string) {
        this.searchText = searchText;
        this.updateFilteredCars(true);
    }

    updateFilteredCars(resetPage: boolean) {
        const searchText = isNullOrUndefined(this.searchText) ? '' : this.searchText.toLowerCase();
        this.filteredCarsData = this.carsData.filter(car =>
            (searchText === '' || this.isCarMatchingSearch(car, searchText))
            && (this.cityFilter === '' || car.CityName === this.cityFilter)
            && (this.companyFilter === '' || car.CompanyName === this.companyFilter)
            && (this.priceFilter === ''
                || (!isNullOrUndefined(car.CarPreferences)
                    && String(car.CarPreferences.PricePerKm) === this.priceFilter))
            && (this.suspendedFilter === ''
                || String(this.isCarSuspended(car)) === this.suspendedFilter)
        );

        if (resetPage) {
            this.currentPage = 1;
        }

        this.clampCurrentPage();
    }

    onCityFilterChanged(value: string) {
        this.cityFilter = value;
        this.updateFilteredCars(true);
    }

    onCompanyFilterChanged(value: string) {
        this.companyFilter = value;
        this.updateFilteredCars(true);
    }

    onPriceFilterChanged(value: string) {
        this.priceFilter = value;
        this.updateFilteredCars(true);
    }

    onSuspendedFilterChanged(value: string) {
        this.suspendedFilter = value;
        this.updateFilteredCars(true);
    }

    getCityOptions() {
        return this.getDistinctOptions(this.carsData, car => car.CityName);
    }

    getCompanyOptions() {
        return this.getDistinctOptions(this.carsData, car => car.CompanyName);
    }

    getPriceOptions(): number[] {
        const prices = this.carsData
            .filter(car => !isNullOrUndefined(car.CarPreferences))
            .map(car => Number(car.CarPreferences.PricePerKm))
            .filter(price => Number.isFinite(price));
        return Array.from(new Set(prices)).sort((left, right) => left - right);
    }

    isCarSuspended(car: Car) {
        return !isNullOrUndefined(car.Suspension) && Boolean(car.Suspension.IsSuspended);
    }

    isCarMatchingSearch(car: Car, searchText: string): boolean {
        if (formatOrderCar(car).toLowerCase().includes(searchText.toLowerCase())) return true;
        if (!isNullOrUndefined(car.CarId) && String(car.CarId).includes(searchText)) {
            return true;
        }

        if (!isNullOrUndefined(car.CarNumber) && car.CarNumber.toLowerCase().includes(searchText)) {
            return true;
        }

        if (!isNullOrUndefined(car.Drivers) && car.Drivers.length > 0) {
            for (const driver of car.Drivers) {
                if (!isNullOrUndefined(driver.DriverName) && driver.DriverName.toLowerCase().includes(searchText)) {
                    return true;
                }
            }
        }

        return searchText.length > 4
            && !isNullOrUndefined(car.DeviceId)
            && car.DeviceId.includes(searchText);
    }

    getPagedCars() {
        const sortedCars = this.tableSort.sort(
            this.filteredCarsData,
            (car, column) => this.getCarSortValue(car, column)
        );
        return sortedCars.slice(this.getPageStartIndex(), this.getPageEndIndex());
    }

    getPageStartIndex(): number {
        return (this.currentPage - 1) * this.pageSize;
    }

    getPageEndIndex(): number {
        return this.getPageStartIndex() + this.pageSize;
    }

    getTotalPages(): number {
        return Math.max(1, Math.ceil(this.filteredCarsData.length / this.pageSize));
    }

    getVisibleRangeStart(): number {
        return this.filteredCarsData.length === 0 ? 0 : this.getPageStartIndex() + 1;
    }

    getVisibleRangeEnd(): number {
        return Math.min(this.getPageEndIndex(), this.filteredCarsData.length);
    }

    goToPreviousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
        }
    }

    goToNextPage() {
        if (this.currentPage < this.getTotalPages()) {
            this.currentPage++;
        }
    }

    changePageSize() {
        this.currentPage = 1;
        this.clampCurrentPage();
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.getTotalPages()) {
            this.currentPage = page;
        }
    }

    clampCurrentPage() {
        if (this.currentPage > this.getTotalPages()) {
            this.currentPage = this.getTotalPages();
        }

        if (this.currentPage < 1) {
            this.currentPage = 1;
        }
    }

    exportData(): void {
        if (!this.permissionService.has(AdminPermissions.ExportData)) {
            return;
        }

        const dataForExport = [];
        for (const car of this.carsData) {
            const dataToAdd = {
                Company: car.CompanyName || '-',
                CarId: car.CarId,
                CarNumber: car.CarNumber,
                LastLogin: car.LastLoginAsString,
                Drivers: this.concatDriverNames(car.Drivers)
            };
            dataForExport.push(dataToAdd);
        }
        this.excelService.exportAsExcelFile(dataForExport, this.externalCars ? 'Cars External' : 'Cars');
    }

    concatDriverNames(drivers: Driver[] | undefined) {
        if (!isNullOrUndefined(drivers) && drivers.length > 0) {
            let driversConcat = '';
            for (const driver of drivers) {
                driversConcat += driver.DriverName + ';';
            }
            return driversConcat;
        } else {
            return '';
        }
    }

    showCarDetails(carItem: Car) {
        this.showDialogForCar(carItem);
    }

    checkIfUserCanExport() {
        this.canExportData = this.permissionService.has(AdminPermissions.ExportData);
    }

    showDialogForCar(carItem: Car) {
        const dialogTexts: { HeaderText: string; carItem: Car; isTest: boolean } = {
            HeaderText: 'Car Details',
            carItem,
            isTest: false
        };

        this.dialogsContainer.clear();
        this.dialogRef = this.dialogsContainer.createComponent(DialogCarComponent);

        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.carItem = dialogTexts.carItem;
        this.dialogRef.instance.isExternal = this.externalCars;

        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onCarUpdated.subscribe(() => {

            this.getCarsData(false);
        });
    }

    private getCarSortValue(car: Car, column: string) {
        switch (column) {
            case 'carId':
                return car.CarId;
            case 'carNumber':
                return car.CarNumber;
            case 'city':
                return car.CityName;
            case 'company':
                return car.CompanyName;
            case 'pricePerKm':
                return car.CarPreferences?.PricePerKm;
            case 'lastLogin':
                return this.getTimeValue(car.LastLoginDate_iso || car.LastLoginDate);
            case 'suspended':
                return !isNullOrUndefined(car.Suspension) && Boolean(car.Suspension.IsSuspended);
            case 'drivers':
                return this.concatDriverNames(car.Drivers);
            default:
                return '';
        }
    }

    private getTimeValue(value: string | Date | undefined) {
        if (isNullOrUndefined(value) || value === '') {
            return undefined;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? undefined : time;
    }

    private getDistinctOptions<T>(items: T[], valueGetter: (item: T) => unknown) {
        const values = items
            .map(valueGetter)
            .filter(value => !isNullOrUndefined(value) && String(value).trim() !== '')
            .map(value => String(value));
        return values.filter((value, index) => values.indexOf(value) === index)
            .sort((left, right) => left.localeCompare(right));
    }

}
